import { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavBarContext } from '../App';
import useCreateChatRoom from '../hooks/messages/useCreateChatRoom';
import { useChatRooms } from '../hooks/messages/useChatRooms';
import { notifyBlockedToast, notifyErrorToast } from '../util/notifyActionError';

const normalizeId = (value) => (value === undefined || value === null ? '' : String(value));

const idsMatch = (left, right) => {
  const normalizedLeft = normalizeId(left);
  const normalizedRight = normalizeId(right);
  return normalizedLeft !== '' && normalizedRight !== '' && normalizedLeft === normalizedRight;
};

const chatRoomMatchesBooking = (room, bookingId) => {
  if (!room || !normalizeId(bookingId)) return false;

  if (idsMatch(room.id, bookingId) || idsMatch(room.linkedBookingID, bookingId)) {
    return true;
  }

  return Array.isArray(room.linkedBookingIDs) && room.linkedBookingIDs.some((linkedId) => idsMatch(linkedId, bookingId));
};

const chatRoomMatchesInquiryPartner = (room, { studioId, userId, currentUserId }) => {
  if (!room || !idsMatch(room.studioID, studioId)) return false;

  const isBookingChat =
    room.isBookingRelated ||
    room.linkedBookingID ||
    (Array.isArray(room.linkedBookingIDs) && room.linkedBookingIDs.length > 0) ||
    room.chatType === 'BOOKING';
  if (isBookingChat) return false;

  const participantIDs = Array.isArray(room.participantIDs) ? room.participantIDs : [];
  const matchesTargetUser =
    idsMatch(room.chatPartner?.id, userId) ||
    idsMatch(room.hostID, userId) ||
    idsMatch(room.guestID, userId) ||
    participantIDs.some((participantId) => idsMatch(participantId, userId));
  const includesCurrentUser =
    idsMatch(room.hostID, currentUserId) ||
    idsMatch(room.guestID, currentUserId) ||
    participantIDs.some((participantId) => idsMatch(participantId, currentUserId));

  return matchesTargetUser && includesCurrentUser;
};

const ChatLink = ({ 
    userId, 
    studioId, 
    studioName, 
    studioImage, 
    className, 
    children, 
    firstMessage,
    creatorNavBar = true,
    chatRoomId,
    bookingId
  }) => {

  const navigate = useNavigate();
  const currentUserId = localStorage?.getItem('userId');
  const { setShowSignIn } = useContext(NavBarContext);
  const { createNewChatRoom, loading: creatingChatRoom } = useCreateChatRoom();
  const { chatRooms, loading: chatRoomsLoading } = useChatRooms(currentUserId);
  const [isLoading, setIsLoading] = useState(false);
  const isBookingLink = !!normalizeId(chatRoomId) || !!normalizeId(bookingId);
  const isWaitingForBookingChat = isBookingLink && !normalizeId(chatRoomId) && chatRoomsLoading;
  const isBusy = isLoading || creatingChatRoom || isWaitingForBookingChat;

  const handleChatClick = async (e) => {
    e.preventDefault();
    if (isBusy) return;
    setIsLoading(true);

    // Check if user is not authenticated
    if (!currentUserId || currentUserId === '' || currentUserId === null) {
      setShowSignIn(true);
      setIsLoading(false);
      return;
    }

    if (currentUserId === userId) {
      setIsLoading(false);
      return;
    }

    try {      
      let targetChatRoomId = normalizeId(chatRoomId);

      if (!targetChatRoomId && normalizeId(bookingId)) {
        const bookingChatRoom = chatRooms.find((room) => chatRoomMatchesBooking(room, bookingId));

        if (bookingChatRoom) {
          targetChatRoomId = bookingChatRoom.id;
        } else {
          notifyErrorToast('Der Chat zu dieser Buchung ist noch nicht verfügbar. Bitte lade die Seite neu und versuche es erneut.', {
            title: 'Chat nicht verfügbar',
          });
          setIsLoading(false);
          return;
        }
      }

      if (!targetChatRoomId) {
        const existingInquiryChatRoom = chatRooms.find((room) => chatRoomMatchesInquiryPartner(room, {
          studioId,
          userId,
          currentUserId,
        }));

        if (existingInquiryChatRoom) {
          targetChatRoomId = existingInquiryChatRoom.id;
        }
      }

      if (!targetChatRoomId) {
        // If no existing chat room, create a new one
        try {
          targetChatRoomId = await createNewChatRoom(
            studioId,
            studioName,
            studioImage,
            currentUserId,
            userId
          );
        } catch (createError) {
          // Check if error is about owning the studio
          const errorMessage = createError?.message || createError?.graphQLErrors?.[0]?.message || '';
          if (errorMessage.includes('eigenen Studio') || errorMessage.includes('own studio')) {
            // User is trying to chat with their own studio - show friendly message
            console.warn('[ChatLink] Cannot create chat for own studio');
            notifyBlockedToast('Du kannst keinen Chat mit deinem eigenen Studio starten.');
            setIsLoading(false);
            return; // Exit early, don't navigate
          }
          
          // For other errors, log and re-throw
          throw createError;
        }
      }

      // Navigate to the chat room (never send messages automatically)
      if (targetChatRoomId) {
        navigate(`/messages/${targetChatRoomId}`, {
          state: { fromProfileMenu: creatorNavBar },
          replace: true
        });
      } else {
        notifyErrorToast('Fehler beim Erstellen des Chats. Bitte versuche es erneut.', {
          title: 'Chat konnte nicht erstellt werden',
        });
      }
    } catch (error) {
      notifyErrorToast('Fehler beim Erstellen des Chats. Bitte versuche es erneut.', {
        title: 'Chat konnte nicht erstellt werden',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <span 
      className={className} 
      onClick={handleChatClick}
      style={{ cursor: isBusy ? 'wait' : 'pointer' }}
    >
      {isBusy ? 'Wird geladen...' : children}
    </span>
  );
};

export default ChatLink;
